import { createTheme } from '@mui/material/styles';

export const getTheme = (mode = 'light') => {
  // Splunk-inspired color palette
  const splunkColors = {
    light: {
      primary: '#0877A6', // Splunk blue
      primaryLight: '#1A91C2',
      primaryDark: '#065F88',
      secondary: '#FF6E00', // Splunk orange
      secondaryLight: '#FF8533',
      secondaryDark: '#CC5800',
      success: '#00A86B',
      warning: '#FFB020',
      error: '#E8114B',
      info: '#0877A6',
      background: {
        default: '#F5F6F7',
        paper: '#FFFFFF',
        elevated: '#FAFBFC',
      },
      text: {
        primary: '#1A1D1F',
        secondary: '#6F7780',
        disabled: '#B0B7BD',
      },
      divider: 'rgba(0, 0, 0, 0.08)',
    },
    dark: {
      primary: '#22A7F0', // Lighter blue for dark mode
      primaryLight: '#4DB8F5',
      primaryDark: '#1A8FCF',
      secondary: '#FF8A3D', // Lighter orange for dark mode
      secondaryLight: '#FFA366',
      secondaryDark: '#E67224',
      success: '#00C878',
      warning: '#FFC94D',
      error: '#FF3366',
      info: '#22A7F0',
      background: {
        default: '#0A0B0D', // Very dark, Splunk-style
        paper: '#171819',
        elevated: '#1F2123',
      },
      text: {
        primary: '#FFFFFF',
        secondary: '#B8BCC1',
        disabled: '#6F7780',
      },
      divider: 'rgba(255, 255, 255, 0.08)',
    },
  };

  const colors = mode === 'light' ? splunkColors.light : splunkColors.dark;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary,
        light: colors.primaryLight,
        dark: colors.primaryDark,
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: colors.secondary,
        light: colors.secondaryLight,
        dark: colors.secondaryDark,
        contrastText: '#FFFFFF',
      },
      success: {
        main: colors.success,
        contrastText: '#FFFFFF',
      },
      warning: {
        main: colors.warning,
        contrastText: mode === 'light' ? '#1A1D1F' : '#FFFFFF',
      },
      error: {
        main: colors.error,
        contrastText: '#FFFFFF',
      },
      info: {
        main: colors.info,
        contrastText: '#FFFFFF',
      },
      background: {
        default: colors.background.default,
        paper: colors.background.paper,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
        disabled: colors.text.disabled,
      },
      divider: colors.divider,
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: 'clamp(2rem, 5vw, 3.5rem)',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontSize: 'clamp(1.75rem, 4vw, 3rem)',
        fontWeight: 700,
        lineHeight: 1.25,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h4: {
        fontSize: 'clamp(1.25rem, 2.5vw, 2rem)',
        fontWeight: 600,
        lineHeight: 1.35,
      },
      h5: {
        fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      button: {
        fontSize: '0.875rem',
        fontWeight: 500,
        textTransform: 'none',
        letterSpacing: '0.01em',
      },
    },
    shape: {
      borderRadius: 8,
    },
    spacing: 8,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // ---- small-screen safety net -------------------------------------
          // Directory values are long and unbreakable: a DN is routinely 60+
          // characters with no spaces, so it cannot wrap and widens whatever
          // cell it lands in. Each table already sits in a TableContainer that
          // scrolls, but only if nothing forces the PAGE wider first.
          'html, body, #root': {
            maxWidth: '100%',
            overflowX: 'hidden',
          },
          '.MuiTableCell-root': {
            // `break-word`, NOT `anywhere`. Both break a long unbroken value,
            // but `anywhere` also shrinks the cell's MIN-CONTENT width, so the
            // table compressed every column to about one character and the
            // headers rendered one letter per line at phone width instead of
            // the container scrolling. `break-word` leaves min-content alone.
            overflowWrap: 'break-word',
          },
          '@media (max-width:900px)': {
            // Below tablet width a directory table cannot fit its columns. Give
            // it a floor so it OVERFLOWS and the TableContainer scrolls, rather
            // than squeezing columns until the text is unreadable.
            '.MuiTableContainer-root .MuiTable-root': { minWidth: 680 },
          },
          '@media (max-width:600px)': {
            // Reclaim space that is pure chrome at phone width.
            '.MuiTableCell-root': { padding: '8px 10px', whiteSpace: 'nowrap' },
            // ...but a DN or an email must still be allowed to wrap.
            '.MuiTableCell-root[data-wrap], .MuiTableCell-root .wrap-anywhere': {
              whiteSpace: 'normal',
            },
            // A dialog should use the screen on a phone rather than float as a
            // narrow card in a dimmed field.
            '.MuiDialog-paper': {
              margin: 8,
              width: 'calc(100% - 16px)',
              maxWidth: 'none',
            },
          },
          // Motion sensitivity is real and every animation here is decorative.
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important',
            },
          },
          body: {
            scrollbarWidth: 'thin',
            scrollbarColor: mode === 'dark' ? '#4F5358 #171819' : '#B0B7BD #F5F6F7',
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: mode === 'dark' ? '#171819' : '#F5F6F7',
            },
            '&::-webkit-scrollbar-thumb': {
              background: mode === 'dark' ? '#4F5358' : '#B0B7BD',
              borderRadius: '4px',
              '&:hover': {
                background: mode === 'dark' ? '#6F7780' : '#6F7780',
              },
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: mode === 'dark'
                ? '0 2px 8px rgba(0, 0, 0, 0.4)'
                : '0 2px 8px rgba(0, 0, 0, 0.1)',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: mode === 'dark'
                ? '0 4px 12px rgba(0, 0, 0, 0.5)'
                : '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            transition: 'box-shadow 0.3s ease-in-out',
          },
          elevation1: {
            boxShadow: mode === 'dark'
              ? '0 1px 3px rgba(0, 0, 0, 0.4)'
              : '0 1px 3px rgba(0, 0, 0, 0.08)',
          },
          elevation2: {
            boxShadow: mode === 'dark'
              ? '0 2px 6px rgba(0, 0, 0, 0.5)'
              : '0 2px 6px rgba(0, 0, 0, 0.1)',
          },
          elevation3: {
            boxShadow: mode === 'dark'
              ? '0 4px 12px rgba(0, 0, 0, 0.6)'
              : '0 4px 12px rgba(0, 0, 0, 0.12)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            border: mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: mode === 'dark'
                ? '0 8px 24px rgba(0, 0, 0, 0.7)'
                : '0 8px 24px rgba(0, 0, 0, 0.15)',
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: mode === 'dark'
              ? '0 2px 8px rgba(0, 0, 0, 0.5)'
              : '0 2px 8px rgba(0, 0, 0, 0.08)',
          },
          colorPrimary: {
            backgroundColor: mode === 'dark' ? colors.background.paper : colors.primary,
            color: mode === 'dark' ? colors.text.primary : '#FFFFFF',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(0, 0, 0, 0.08)',
            backgroundColor: mode === 'dark' ? colors.background.paper : colors.background.elevated,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark'
              ? 'rgba(255, 255, 255, 0.03)'
              : 'rgba(0, 0, 0, 0.02)',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid rgba(0, 0, 0, 0.08)',
          },
          head: {
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '6px',
            fontWeight: 500,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderWidth: '1.5px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            margin: '2px 8px',
            '&.Mui-selected': {
              backgroundColor: mode === 'dark'
                ? 'rgba(34, 167, 240, 0.15)'
                : 'rgba(8, 119, 166, 0.08)',
              '&:hover': {
                backgroundColor: mode === 'dark'
                  ? 'rgba(34, 167, 240, 0.2)'
                  : 'rgba(8, 119, 166, 0.12)',
              },
            },
          },
        },
      },
    },
  });
};

// Default light theme for backwards compatibility
const theme = getTheme('light');
export default theme;
